import React from "react";
import { useTheme } from "react-jss";
import { useSelector } from "react-redux";
import classnames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { xor, isEqual } from "../../utils/lodash";
import useStyles from "./Select.styles";
import { RecalledText } from "../../containers/shared/RecalledText";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import useRecall from "../../hooks/useRecall";
import { removeHtmlTags } from "../../utils";
import InputWrapper from "../shared/InputWrapper";
import useQuestion from "../../hooks/useQuestion";
import { QuestionType } from "../../models/enums/QuestionType";
import { useKeyboardNavigation } from "../../hooks/useKeyboardNavigation";
import { DefaultNavigation } from "../controls/KeyboardNavigation/DefaultNavigation";
import { VariableModel } from "../../models/VariableModel";
import type { Theme } from "../../types/Theme";
import { StoreState } from "../../models/states/StoreState";
import { shouldUseModernTheme } from "../../theme/navigationButtonThemeColors";
import { shouldSuppressModernHybridGridInputWrapperMargin } from "../../theme/gridThemeTokens";
import {
    isGlobeIcon,
    ModernSelectChevronDownIcon,
    ModernSelectChevronUpIcon,
    ModernSelectGlobeIcon,
} from "../shared/ModernSelectIcons";
import {
    hasRealSelectValue,
    isEmptyPlaceholderOption,
    isListOptionSelected,
} from "./selectOptionUtils";

export interface Option {
    id: string;
    label: string;
    value: string;
    isNonInteractive?: boolean;
}

interface Props {
    id: string;
    labelId?: string;
    /** When set (e.g. hybrid grid), used as aria-label instead of aria-labelledby so names work when header ids are not exposed. */
    accessibleName?: string;
    className?: string;
    required?: boolean;
    multiple?: boolean;
    value: string[];
    options: Option[];
    onChange(value: string[]): void;
    icon?: IconProp;
    disabled?: boolean;
    /** When disabled, keep label/icon at full opacity and only mute the border (language selector). */
    disabledBorderOnly?: boolean;
    size?: string;
    listboxAriaLabelId?: string;
    ariaDisabledBy?: boolean;
    variable?: VariableModel;
    hasError?: boolean;
}

interface OptionProps {
    option: Option;
    typeAhead: string;
    multiple?: boolean;
    selected?: boolean;
    assistiveAriaSelected?: boolean;
    isKeyboardActiveDescendant?: boolean;
    elementReference: React.RefObject<HTMLDivElement>;
    onSelectOption(option: Option);
    onDetectCharacter(key: string);
    onKeyDown(e: React.KeyboardEvent);
    onCloseListBox();
    onSelectPreviousOption();
    onSelectNextOption();
    onSelectFirstOption();
    onSelectLastOption();
}

enum SelectionDirection {
    Previous,
    Next,
}

function ListBoxOption(props: OptionProps) {
    const {
        option,
        typeAhead,
        multiple,
        selected,
        assistiveAriaSelected,
        isKeyboardActiveDescendant,
        onDetectCharacter,
        onSelectOption,
        onSelectPreviousOption,
        onKeyDown,
        onSelectNextOption,
        onSelectFirstOption,
        onSelectLastOption,
        onCloseListBox,
        elementReference,
    } = props;

    const theme = useTheme() as Theme;
    const classes = useStyles({ theme } as any);
    const useModern = shouldUseModernTheme(theme);
    const isPlaceholder = isEmptyPlaceholderOption(option);

    function onOptionKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Enter" || (e.key === " " && !typeAhead.length)) {
            if (option.isNonInteractive) {
                e.preventDefault();
                return;
            }
            onSelectOption(option);
            if (!multiple) {
                onCloseListBox();
                elementReference.current.focus();
            }
            e.preventDefault();
            return;
        }
        if (e.key === "Escape") {
            elementReference.current.focus();
            onCloseListBox();
            e.preventDefault();
            return;
        }
        if (e.key === "ArrowUp") {
            onSelectPreviousOption();
            e.preventDefault();
            return;
        }
        if (e.key === "ArrowDown") {
            onSelectNextOption();
            e.preventDefault();
            return;
        }
        if (e.key === "Home") {
            onSelectFirstOption();
            e.preventDefault();
            return;
        }
        if (e.key === "End") {
            onSelectLastOption();
            e.preventDefault();
            return;
        }
        if (e.key === "Tab") {
            onKeyDown(e);
            e.preventDefault();
        }
        if (onDetectCharacter(e.key)) {
            e.preventDefault();
        }
    }

    function onClickOption(e: React.MouseEvent) {
        if (option.isNonInteractive) {
            e.preventDefault();
            return;
        }
        onSelectOption(option);
        if (!multiple) {
            onCloseListBox();
        }
        e.preventDefault();
    }

    return (
        <li
            id={option.id}
            key={option.value}
            role="option"
            onClick={onClickOption}
            onKeyDown={onOptionKeyDown}
            className={classnames(
                option.isNonInteractive
                    ? (classes as any).optionNonInteractive
                    : useModern && isPlaceholder && !isKeyboardActiveDescendant
                      ? (classes as any).optionPlaceholderModern
                      : useModern
                        ? (classes as any).optionModern
                        : (classes as any).option,
                useModern && selected && !isPlaceholder && (classes as any).optionSelectedModern,
                useModern &&
                    isKeyboardActiveDescendant &&
                    (classes as any).optionHighlightedModern,
                !useModern && isKeyboardActiveDescendant && (classes as any).optionHighlighted,
            )}
            tabIndex={-1}
            aria-label={removeHtmlTags(option.label)}
            aria-selected={
                assistiveAriaSelected !== undefined
                    ? assistiveAriaSelected
                    : option.isNonInteractive
                      ? undefined
                      : selected
            }
        >
            <RecalledText text={option.label} ariaHidden={true} />
            <div className="flex" />
            {multiple && (
                <FontAwesomeIcon
                    icon={["far", "check"]}
                    className={classnames(
                        (classes as any).check,
                        !selected && (classes as any).invisible,
                    )}
                />
            )}
        </li>
    );
}

export default function Select(props: Props) {
    const {
        options,
        multiple,
        required,
        id,
        labelId,
        accessibleName,
        className,
        value,
        onChange,
        size,
        icon,
        disabled,
        disabledBorderOnly,
        listboxAriaLabelId,
        ariaDisabledBy,
        variable,
        hasError = false,
    } = props;
    const question = useQuestion();
    const isHybridGrid =
        question && question?.parent && question?.parent?.type === QuestionType.HybridGrid;

    const { elementReference, onKeyDown } = useKeyboardNavigation(
        id,
        variable,
        question,
        DefaultNavigation,
        null,
    );
    const optionsRef = React.useRef(options);
    const valueRef = React.useRef(value);
    const selectedOptions = React.useMemo(() => {
        const selected = options.filter((o) => o.value && value.includes(o.value));
        if (!selected.length) {
            return [options[0]];
        }
        return selected;
    }, [options, value]);

    const displayedOptions = React.useMemo(() => {
        if (multiple) {
            return options.filter((option) => option.value !== "");
        }
        return options;
    }, [options, multiple]);

    const defaultFocusedOption = displayedOptions.includes(selectedOptions[0])
        ? selectedOptions[0]
        : displayedOptions[0];

    const ref = React.useRef<HTMLDivElement>(null);
    const listboxId = `${id}_listbox`;

    const theme = useTheme() as Theme;
    const validation = useSelector((state: StoreState) => state.interview.validation);
    const validationHasError = Boolean(
        validation?.errorsByVariableName &&
            ((variable?.name &&
                (validation.errorsByVariableName[variable.name]?.length ?? 0) > 0) ||
                (question?.type === QuestionType.DrillDown &&
                    question.name &&
                    (validation.errorsByVariableName[question.name]?.length ?? 0) > 0)),
    );
    const inError = hasError || validationHasError;
    const hasSelectedValue = multiple
        ? hasRealSelectValue(value)
        : selectedOptions.length > 0 && !isEmptyPlaceholderOption(selectedOptions[0]);
    const classes = useStyles({
        theme,
        icon,
        size,
        disabled,
        selected: hasSelectedValue,
    } as any);
    const useModernTheme = shouldUseModernTheme(theme);

    const [dropdownWidth, setDropdownWidth] = React.useState(0);
    const [hovered, setHovered] = React.useState(false);
    const [focused, setFocused] = React.useState(false);
    const [expanded, setExpanded] = React.useState(false);
    const [typeAhead, setTypeAhead] = React.useState("");
    const [focusedOption, setFocusedOption] = React.useState(defaultFocusedOption);
    const [active, setActive] = React.useState(false);
    const [keyboardFocused, setKeyboardFocused] = React.useState(false);
    const [keyboardListHighlight, setKeyboardListHighlight] = React.useState(false);
    const focusFromPointerRef = React.useRef(false);
    const ariaAttributes = {};

    if (listboxAriaLabelId) {
        ariaAttributes["aria-labelledby"] = listboxAriaLabelId;
    }

    if (ariaDisabledBy) {
        ariaAttributes["aria-disabled"] = ariaDisabledBy;
    }

    React.useEffect(() => {
        valueRef.current = value;
    }, [value]);

    React.useEffect(() => {
        optionsRef.current = options;
    }, [options]);

    React.useLayoutEffect(() => {
        if (!expanded) {
            return undefined;
        }
        function measure() {
            const rect = ref.current.getBoundingClientRect();
            if (!isEqual(dropdownWidth, rect.width)) {
                setDropdownWidth(rect.width);
            }
        }
        measure();
    }, [expanded]);

    React.useEffect(() => {
        // Set the focused option to the selected option when the control is not multi-selectable.
        if (!multiple) {
            setFocusedOption(selectedOptions[0]);
        }
    }, [multiple, selectedOptions]);

    React.useLayoutEffect(() => {
        if (!multiple || !expanded || !focusedOption) {
            return undefined;
        }
        const el = document.getElementById(focusedOption.id);
        if (el) {
            el.focus();
        }
        return undefined;
    }, [multiple, expanded, focusedOption]);

    function focusOption(option) {
        setFocusedOption(option);
    }

    const selectOption = React.useCallback(
        (option) => {
            // If the select component is inside a Hybrid Grid, adapt the scrollable container style
            if (ref && ref.current) {
                toggleContainerStyle(!expanded);
            }
            if (multiple) {
                onChange(xor(valueRef.current, [option.value]));
            } else {
                onChange([option.value]);
            }
        },
        [expanded, multiple, onChange],
    );

    React.useEffect(() => {
        let timeout;
        if (typeAhead.length) {
            timeout = setTimeout(function resetTypeAhead() {
                setTypeAhead("");
            }, 500);
            const nextMatchedOption = optionsRef.current.find((o) =>
                o.label.toLowerCase().startsWith(typeAhead.toLowerCase()),
            );
            if (nextMatchedOption) {
                setFocusedOption(nextMatchedOption);
                setKeyboardListHighlight(true);
            }
        }
        return function cleanup() {
            if (timeout) {
                window.clearTimeout(timeout);
            }
        };
    }, [typeAhead, setFocusedOption]);

    React.useEffect(() => {
        function clickOutside(event) {
            if (ref.current?.contains(event.target)) {
                return;
            }
            closeListBox();
        }
        document.addEventListener("mousedown", clickOutside);
        return function cleanup() {
            document.removeEventListener("mousedown", clickOutside);
        };
    }, []);

    function toggleContainerStyle(showDropDown: boolean) {
        const scrollableWindow = ref.current.closest(".scrollable__window");
        const scrollbarsDiv = ref.current.closest(".scrollbars");

        if (scrollableWindow && scrollbarsDiv) {
            const scrollableWindowElement = scrollableWindow as HTMLElement;
            const scrollbarsDivElement = scrollbarsDiv as HTMLElement;

            if (showDropDown) {
                scrollableWindowElement.style.setProperty("overflow", "visible", "important");
                scrollbarsDivElement.style.setProperty("overflow", "visible", "important");
                (scrollbarsDiv.lastChild as HTMLElement)?.style.setProperty("z-index", "-99");
            } else {
                scrollableWindowElement.style.setProperty("overflow", "auto", "important");
                scrollbarsDivElement.style.setProperty("overflow", "auto", "important");
                (scrollbarsDiv.lastChild as HTMLElement)?.style.removeProperty("z-index");
            }
        }
    }

    function getAdjacentSelectableOption(option: Option, direction: SelectionDirection) {
        const focusedIndex = displayedOptions.indexOf(option);
        const isPrevious = direction === SelectionDirection.Previous;

        const adjacentIndices = Array.from(
            Array(Math.abs(focusedIndex - (isPrevious ? 0 : displayedOptions.length - 1))).keys(),
        ).map((index) =>
            isPrevious ? Math.abs(index + 1 - focusedIndex) : index + focusedIndex + 1,
        );

        const adjacent = adjacentIndices.find((index) => !displayedOptions[index].isNonInteractive);
        return adjacent !== undefined ? displayedOptions[adjacent] : option;
    }

    function selectFirstOption() {
        const [firstOption] = displayedOptions;
        const target = firstOption.isNonInteractive
            ? getAdjacentSelectableOption(firstOption, SelectionDirection.Next)
            : firstOption;
        if (multiple) {
            selectOption(target);
        } else {
            focusOption(target);
        }
    }

    function selectLastOption() {
        const lastOption = displayedOptions[displayedOptions.length - 1];
        const target = lastOption.isNonInteractive
            ? getAdjacentSelectableOption(lastOption, SelectionDirection.Previous)
            : lastOption;
        if (multiple) {
            selectOption(target);
        } else {
            focusOption(target);
        }
    }

    function selectPreviousOption() {
        const previous = getAdjacentSelectableOption(focusedOption, SelectionDirection.Previous);
        focusOption(previous);
    }

    function selectNextOption() {
        const next = getAdjacentSelectableOption(focusedOption, SelectionDirection.Next);
        focusOption(next);
    }

    function closeListBox() {
        if (ref.current) {
            toggleContainerStyle(false);
        }
        setExpanded(false);
        setFocusedOption(defaultFocusedOption);
        setKeyboardListHighlight(false);
    }

    function openListBox() {
        if (ref.current) {
            toggleContainerStyle(true);
        }
        setExpanded(true);
        setFocusedOption(
            displayedOptions.includes(selectedOptions[0])
                ? selectedOptions[0]
                : displayedOptions[0],
        );
    }

    function moveHighlight(direction: SelectionDirection) {
        if (ref.current) {
            toggleContainerStyle(true);
        }
        setExpanded(true);
        setKeyboardListHighlight(true);
        setFocusedOption((prev) => {
            const base = expanded ? prev : defaultFocusedOption;
            return getAdjacentSelectableOption(base, direction);
        });
    }

    function detectCharacter(key: string) {
        if (/^[\w\d\s]$/i.test(key)) {
            setTypeAhead(typeAhead + key);
            return true;
        }
        return false;
    }

    function onButtonKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Enter") {
            if (!multiple && expanded) {
                selectOption(focusedOption);
                setExpanded(false);
                e.preventDefault();
                return;
            }
            if (expanded) {
                closeListBox();
            } else {
                openListBox();
            }
            e.preventDefault();
            return;
        }
        if (e.key === " " && !typeAhead.length) {
            if (!multiple && expanded) {
                selectOption(focusedOption);
                setExpanded(false);
                e.preventDefault();
                return;
            }
            if (expanded) {
                closeListBox();
            } else {
                openListBox();
            }
            e.preventDefault();
            return;
        }
        if (e.key === "Escape" && expanded) {
            closeListBox();
            e.preventDefault();
            return;
        }
        // Hybrid grid keeps plain ArrowUp/Down for row moves while collapsed; Alt+ArrowDown must
        // still open the combobox (native pattern) and must not bubble to grid navigation.
        if (isHybridGrid && !expanded && e.altKey && e.key === "ArrowDown") {
            openListBox();
            e.preventDefault();
            return;
        }
        if (!isHybridGrid || (isHybridGrid && expanded)) {
            if (e.key === "ArrowUp") {
                moveHighlight(SelectionDirection.Previous);
                e.preventDefault();
                return;
            }
            if (e.key === "ArrowDown") {
                moveHighlight(SelectionDirection.Next);
                e.preventDefault();
                return;
            }
        }
        if (!multiple && expanded && (e.key === "Home" || e.key === "End")) {
            setKeyboardListHighlight(true);
            if (e.key === "Home") {
                selectFirstOption();
            } else {
                selectLastOption();
            }
            e.preventDefault();
            return;
        }

        if (detectCharacter(e.key)) {
            e.preventDefault();
        }
    }

    function handleKeyDown(event) {
        onButtonKeyDown(event);
        // Hybrid grid registers ArrowUp/ArrowDown for row moves; when the list is open we handle
        // arrows in onButtonKeyDown (moveHighlight) and call preventDefault — skip grid navigation.
        if (!event.defaultPrevented) {
            onKeyDown(event);
        }
    }

    function onClickSelect(e: React.MouseEvent) {
        if (disabled !== true) {
            if (expanded) {
                closeListBox();
            } else {
                setKeyboardListHighlight(false);
                openListBox();
            }
            e.preventDefault();
        }
    }

    const ariaLabelledForDivIds =
        typeof labelId === "string" && labelId.length > 0 ? labelId : undefined;

    const comboboxNameProps =
        typeof accessibleName === "string" && accessibleName.length > 0
            ? ({ "aria-label": accessibleName } as const)
            : ({ "aria-labelledby": ariaLabelledForDivIds } as const);

    const listBoxChildren = displayedOptions.map((option) => {
        let assistiveAriaSelected: boolean | undefined;
        if (!multiple) {
            if (expanded) {
                assistiveAriaSelected = focusedOption?.id === option.id;
            } else if (option.isNonInteractive) {
                assistiveAriaSelected = selectedOptions.includes(option) ? true : undefined;
            } else {
                assistiveAriaSelected = selectedOptions.includes(option);
            }
        }
        const isKeyboardActiveDescendant =
            !multiple &&
            expanded &&
            keyboardListHighlight &&
            focusedOption?.id === option.id;

        return (
            <ListBoxOption
                key={option.id}
                option={option}
                typeAhead={typeAhead}
                multiple={multiple}
                selected={isListOptionSelected(option, value)}
                assistiveAriaSelected={assistiveAriaSelected}
                isKeyboardActiveDescendant={isKeyboardActiveDescendant}
                elementReference={elementReference}
                onSelectOption={selectOption}
                onDetectCharacter={detectCharacter}
                onKeyDown={onKeyDown}
                onCloseListBox={closeListBox}
                onSelectPreviousOption={selectPreviousOption}
                onSelectNextOption={selectNextOption}
                onSelectFirstOption={selectFirstOption}
                onSelectLastOption={selectLastOption}
            />
        );
    });

    const recalledLabel = useRecall(selectedOptions[0] && selectedOptions[0].label);

    const activeDescendantId = !multiple
        ? expanded && focusedOption
            ? focusedOption.id
            : selectedOptions[0]?.id
        : undefined;

    return (
        <InputWrapper
            size={size}
            suppressTopMargin={shouldSuppressModernHybridGridInputWrapperMargin(
                theme,
                Boolean(isHybridGrid),
            )}
        >
            <div
                ref={ref}
                className={classnames(
                    className,
                    (classes as any).select,
                    disabled &&
                        disabledBorderOnly &&
                        (classes as any).selectDisabledBorderOnly,
                    disabled && !disabledBorderOnly && (classes as any).selectDisabled,
                    hovered && !focused && !expanded && (classes as any).selectHover,
                    expanded && (classes as any).selectExpanded,
                    focused && !expanded && (classes as any).selectFocused,
                    useModernTheme && keyboardFocused && !expanded && (classes as any).selectKeyboardFocus,
                    active && !useModernTheme && (classes as any).choiceActive,
                )}
                id={id}
                onMouseEnter={() => !disabled && setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onFocus={() => setActive(true)}
                onBlur={(e) => {
                    setActive(false);
                    const target = e.relatedTarget || document.activeElement;
                    // If the element that *gained* focus is not contained within, close the list box.
                    if (!ref.current.contains(target as Node)) {
                        closeListBox();
                    }

                    if (ref && ref.current) {
                        toggleContainerStyle(expanded);
                    }
                }}
            >
                {icon &&
                    (useModernTheme && isGlobeIcon(icon) ? (
                        <ModernSelectGlobeIcon
                            className={classnames(
                                (classes as any).icon,
                                (classes as any).iconLeft,
                                (classes as any).iconSvg,
                                (classes as any).iconSvgGlobe,
                            )}
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={icon}
                            className={classnames(
                                (classes as any).icon,
                                (classes as any).iconLeft,
                            )}
                            onFocus={() => setActive(true)}
                        />
                    ))}
                <div
                    tabIndex={disabled ? -1 : 0}
                    role="combobox"
                    ref={elementReference}
                    aria-controls={listboxId}
                    aria-activedescendant={activeDescendantId}
                    {...comboboxNameProps}
                    aria-disabled={ariaDisabledBy}
                    aria-invalid={inError || undefined}
                    className={classnames(
                        (classes as any).selectContent,
                        useModernTheme &&
                            isGlobeIcon(icon) &&
                            (classes as any).selectContentGlobe,
                    )}
                    onClick={onClickSelect}
                    onKeyDown={handleKeyDown}
                    onMouseDown={() => {
                        focusFromPointerRef.current = true;
                    }}
                    onBlur={() => {
                        if (disabled !== true) {
                            setFocused(false);
                            setKeyboardFocused(false);
                        }
                    }}
                    onFocus={() => {
                        if (disabled !== true) {
                            setFocused(true);
                            if (useModernTheme) {
                                setKeyboardFocused(!focusFromPointerRef.current);
                                focusFromPointerRef.current = false;
                            }
                        }
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={expanded ? "true" : "false"}
                    dangerouslySetInnerHTML={{ __html: recalledLabel }}
                />
                {disabled !== true &&
                    (useModernTheme ? (
                        expanded ? (
                            <ModernSelectChevronUpIcon
                                className={classnames(
                                    (classes as any).icon,
                                    (classes as any).iconCaret,
                                    (classes as any).iconSvg,
                                    (classes as any).iconSvgCaret,
                                )}
                            />
                        ) : (
                            <ModernSelectChevronDownIcon
                                className={classnames(
                                    (classes as any).icon,
                                    (classes as any).iconCaret,
                                    (classes as any).iconSvg,
                                    (classes as any).iconSvgCaret,
                                )}
                            />
                        )
                    ) : (
                        <FontAwesomeIcon
                            icon={expanded ? "caret-up" : "caret-down"}
                            className={classnames(
                                (classes as any).icon,
                                (classes as any).iconCaret,
                            )}
                        />
                    ))}
                <ul
                    id={listboxId}
                    role="listbox"
                    className={classnames(
                        (classes as any).listbox,
                        (classes as any).dropDown,
                        !expanded && (classes as any).hidden,
                        expanded && (classes as any).listboxExpanded,
                    )}
                    style={{ minWidth: dropdownWidth }}
                    tabIndex={-1}
                    aria-required={required}
                    {...ariaAttributes}
                >
                    {listBoxChildren}
                </ul>
            </div>
        </InputWrapper>
    );
}
